import { lazy, type ComponentType, type LazyExoticComponent } from "react";
import {
  ARTICLES,
  ARTICLE_CATEGORIES,
  type ArticleCategory,
  type ArticleMeta,
} from "../content/articles/meta";

export { ARTICLE_CATEGORIES };
export type { ArticleCategory, ArticleMeta };

type ArticleBody = LazyExoticComponent<ComponentType>;

const loaders = import.meta.glob<{ default: ComponentType }>(
  "../content/articles/*.tsx"
);

// Built once at module load so no lazy component is created during render.
const bodies = new Map<string, ArticleBody>();
for (const [path, load] of Object.entries(loaders)) {
  const slug = path.replace(/^.*\//, "").replace(/\.tsx$/, "");
  bodies.set(slug, lazy(load));
}

const byNewest = (a: ArticleMeta, b: ArticleMeta) =>
  Date.parse(b.publishedAt) - Date.parse(a.publishedAt);

export function getAllArticles(): ArticleMeta[] {
  return [...ARTICLES].sort(byNewest);
}

export function getArticleBySlug(slug: string): ArticleMeta | undefined {
  return ARTICLES.find((a) => a.slug === slug);
}

export function getArticleBody(slug: string): ArticleBody | undefined {
  return bodies.get(slug);
}

export function getRelatedArticles(
  article: ArticleMeta,
  limit = 3
): ArticleMeta[] {
  const tags = new Set(article.tags);
  return getAllArticles()
    .filter((a) => a.slug !== article.slug)
    .map((a) => ({
      a,
      sameCategory: a.category === article.category,
      shared: a.tags.filter((t) => tags.has(t)).length,
    }))
    .filter((x) => x.sameCategory || x.shared > 0)
    .sort(
      (x, y) =>
        Number(y.sameCategory) - Number(x.sameCategory) ||
        y.shared - x.shared
    )
    .slice(0, limit)
    .map((x) => x.a);
}

export function getAdjacentArticles(article: ArticleMeta): {
  previous?: ArticleMeta;
  next?: ArticleMeta;
} {
  const all = getAllArticles();
  const i = all.findIndex((a) => a.slug === article.slug);
  if (i === -1) return {};
  // "previous" is the older article, "next" the newer one, in published order.
  return { previous: all[i + 1], next: all[i - 1] };
}
